import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../firebaseConfig';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import PasswordInput from './PasswordInput';
import TextInput from './TextInput';
import { useTranslation } from '../i18n';
import closeIcon from '../images/close-line.svg';
import ToS from './ToS';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import PrivacyPolicy from './PrivacyPolicy';

async function writeUserData(uid, email, name) {
  const user = {
    uid: uid,
    name: name,
    email: email,
    achievements: [],
    sessions: [],
    currentlyReading: {},
    createdAt: serverTimestamp(),
  };
  await setDoc(doc(db, 'Users', uid), user);
}

const SignUp = ({ showNotification }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [eula, setEULA] = useState(false);
  const [dialogIsOpen, setDialogOpen] = useState(false);

  const dialogRef = useRef(null);

  const openDialog = () => {
    if (dialogRef.current) {
      setDialogOpen(true);
      dialogRef.current.showModal();
    }
  };

  const closeDialog = (accept) => {
    setEULA(accept);
    if (dialogRef.current) {
      setDialogOpen(false);
      dialogRef.current.close();
    }
  };

  const handleNameChange = (e) => {
    setName(e.target.value);
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
  };

  const handleEULAChange = (e) => {
    setEULA(e.target.checked);
  };

  const isValidEmail = (email) => {
    // Email validation logic here (e.g., regex)
    // Return true if valid, false otherwise
    return /\S+@\S+\.\S+/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      showNotification(t.passwordmissmatch);
      return;
    }
    if (!isValidEmail(email)) {
      showNotification(t.invalidemail);
      return;
    }

    await createUserWithEmailAndPassword(auth, email, password)
      .then((userCredential) => {
        // Signed in and save user to db
        writeUserData(userCredential.user.uid, email, name);
        navigate('/search');
      })
      .catch((error) => {
        const errorMessage = error.message;
        showNotification(errorMessage);
      });
  };

  return [
    <form onSubmit={handleSubmit} className="form" key="form">
      <h1 className="h1">{t.registerMessage}</h1>

      <TextInput
        type="text"
        id="name"
        value={name}
        onChangeFunction={handleNameChange}
        labelName={t.nameLabel}
        required={true}
      />

      <TextInput
        type="email"
        id="email"
        value={email}
        onChangeFunction={handleEmailChange}
        labelName={t.emailLabel}
        required={true}
        inputmode="email"
      />

      <PasswordInput
        labelName={t.passwordLabel}
        id="password"
        onChangeFunction={handlePasswordChange}
        value={password}
      />

      <PasswordInput
        labelName={t.confirmpasswordLabel}
        id="confirmPassword"
        onChangeFunction={handleConfirmPasswordChange}
        value={confirmPassword}
      />

      <div className="form-floating form-checkbox">
        <div className="form-control">
          <input
            className="checkbox"
            type="checkbox"
            id="eula"
            checked={eula}
            onChange={handleEULAChange}
            required
          />
          <div>
            {t.acceptterms}
            {` `}
            <span
              className="link"
              role="link"
              onClick={() => openDialog()}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  openDialog();
                }
              }}
              tabIndex={0}
            >
              {t.terms}
            </span>
          </div>
        </div>
        <label htmlFor="eula" className="form-label">
          {t.termsofservice}
        </label>
      </div>

      <div className="btn-group">
        <button className="btn btn-primary w-100 py-2" type="submit">
          {t.register}
        </button>
      </div>
    </form>,
    <dialog ref={dialogRef} key="dialog">
      {dialogIsOpen && (
        <div className="fullscreen-container signup" tabIndex={-1}>
          <button
            className={`fullscreen-close`}
            onClick={() => closeDialog(eula)}
          >
            <img src={closeIcon} width={42} height={42} alt={t.close} />
            {t.close}
          </button>

          <div className="card-wrapper">
            <Tabs>
              <TabList className="tabs">
                <Tab className="tab fs-l">{t.termsofservice}</Tab>
                <Tab className="tab fs-l">{t.privacypolicy}</Tab>
              </TabList>

              <TabPanel>
                <div className="card">
                  <ToS />
                </div>
              </TabPanel>
              <TabPanel>
                <div className="card">
                  <PrivacyPolicy />
                </div>
              </TabPanel>
            </Tabs>
          </div>

          <div className="btn-group-wrapper">
            <div className="btn-group btn-group-two-col">
              <button
                className="btn btn-secondary w-100"
                onClick={() => closeDialog(false)}
              >
                {t.declinetheterms}
              </button>
              <button
                className="btn btn-primary w-100"
                onClick={() => closeDialog(true)}
              >
                {t.accepttheterms}
              </button>
            </div>
          </div>
        </div>
      )}
    </dialog>,
  ];
};

export default SignUp;
